import React, { useState } from 'react';
import {
  Checkbox,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Label,
  LabelGroup,
  TextInput,
} from '@patternfly/react-core';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface NotificationRecipientsFieldProps {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  recipients: string[];
  onRecipientsChange: (recipients: string[]) => void;
  hasUnchipifiedInput: boolean;
  onHasUnchipifiedInputChange: (hasInput: boolean) => void;
}

function NotificationRecipientsField({
  enabled,
  onEnabledChange,
  recipients,
  onRecipientsChange,
  onHasUnchipifiedInputChange,
}: NotificationRecipientsFieldProps) {
  const [buffer, setBuffer] = useState('');
  const [inlineMessage, setInlineMessage] = useState<{ text: string; variant: 'error' | 'warning' } | null>(null);

  const updateBuffer = (value: string) => {
    setBuffer(value);
    onHasUnchipifiedInputChange(value.trim().length > 0);
    if (inlineMessage) setInlineMessage(null);
  };

  const commitBuffer = () => {
    const trimmed = buffer.trim();
    if (trimmed.length === 0) {
      setInlineMessage(null);
      return;
    }

    if (!EMAIL_REGEX.test(trimmed)) {
      setInlineMessage({ text: `${trimmed} is not a valid email address`, variant: 'error' });
      return;
    }

    if (recipients.some((r) => r.toLowerCase() === trimmed.toLowerCase())) {
      setInlineMessage({ text: `${trimmed} is already in the list`, variant: 'warning' });
      setBuffer('');
      onHasUnchipifiedInputChange(false);
      return;
    }

    onRecipientsChange([...recipients, trimmed]);
    setBuffer('');
    onHasUnchipifiedInputChange(false);
    setInlineMessage(null);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitBuffer();
    } else if (event.key === ',') {
      event.preventDefault();
      commitBuffer();
    } else if (event.key === 'Backspace' && buffer.length === 0 && recipients.length > 0) {
      onRecipientsChange(recipients.slice(0, -1));
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text');
    const fragments = pasted.split(/[\s,;]+/).filter((f) => f.length > 0);
    const valid: string[] = [];
    const invalid: string[] = [];

    for (const fragment of fragments) {
      if (!EMAIL_REGEX.test(fragment)) {
        invalid.push(fragment);
      } else if (
        recipients.some((r) => r.toLowerCase() === fragment.toLowerCase()) ||
        valid.some((v) => v.toLowerCase() === fragment.toLowerCase())
      ) {
        // skip duplicates
      } else {
        valid.push(fragment);
      }
    }

    if (valid.length > 0) {
      onRecipientsChange([...recipients, ...valid]);
    }

    const newBuffer = invalid.join(' ');
    setBuffer(newBuffer);
    onHasUnchipifiedInputChange(newBuffer.trim().length > 0);

    if (invalid.length > 0) {
      setInlineMessage({
        text:
          invalid.length === 1
            ? `${invalid[0]} is not a valid email address`
            : `${invalid.length} entries are not valid email addresses`,
        variant: 'error',
      });
    } else {
      setInlineMessage(null);
    }
  };

  const removeRecipient = (index: number) => {
    onRecipientsChange(recipients.filter((_, i) => i !== index));
  };

  return (
    <FormGroup label="Notification" fieldId="notification-recipients">
      <Checkbox
        id="notification-recipients"
        label="Send credentials by email"
        description="The email contains the password in plaintext."
        isChecked={enabled}
        onChange={(_event, checked) => onEnabledChange(checked)}
      />
      {enabled && (
        <div className="pf-v6-u-mt-sm">
          {recipients.length > 0 && (
            <LabelGroup className="pf-v6-u-mb-sm">
              {recipients.map((email, index) => (
                <Label key={email} onClose={() => removeRecipient(index)}>
                  {email}
                </Label>
              ))}
            </LabelGroup>
          )}
          <TextInput
            id="notification-recipients-input"
            type="email"
            autoComplete="off"
            value={buffer}
            onChange={(_event, value) => updateBuffer(value)}
            onKeyDown={handleKeyDown}
            onBlur={() => commitBuffer()}
            onPaste={handlePaste}
            placeholder="Add email address"
          />
          {inlineMessage ? (
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant={inlineMessage.variant}>{inlineMessage.text}</HelperTextItem>
              </HelperText>
            </FormHelperText>
          ) : (
            <FormHelperText>
              <HelperText>
                <HelperTextItem>
                  Press Enter or comma to add. Backspace on empty input removes the last recipient.
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          )}
        </div>
      )}
    </FormGroup>
  );
}

export default NotificationRecipientsField;
